// TelcoTrack — hard-block validation before signature package / production PDF (P0.3).

import type { BremenaContractInstrument, BremenaOwnerInput } from './bremena-contract.types';
import { requiredSignerSlots } from './bremena-signatures.util';
import { normalizeBremenaContractStatus } from '../constants/bremena.constants';

const DOPLNIT = '[DOPLNIT]';
const BULLET_MARK = '[●]';
/** Underscores that look like unfilled blanks (signature ink lines excluded separately). */
const BLANK_UNDERSCORE_RE = /_{3,}/g;
/** Unresolved alternative clauses left in CETIN text (choose one). */
const CONFLICTING_ALT_RE =
  /radou\s*\/\s*zastupitelstvem|zastupitelstvem\s*\/\s*radou|varianta\s*[ab]\s*[:./]|\/\s*nebo\s+|\[\s*DOPLNIT\s*\]\s*\/\s*\[\s*DOPLNIT\s*\]/gi;

/** CharOsKod 20 = právnická osoba (vč. městská s.r.o.) — doložka rady / valné hromady. */
const CHAROS_PRIVATE_COMPANY = '20';

export interface BremenaValidationIssue {
  code: string;
  messageCs: string;
}

export interface BremenaValidationReport {
  readyForSignature: boolean;
  errors: string[];
  warnings: string[];
  validatedTemplateVersion: string;
  validatedAt: string;
  documentHash: string;
}

export interface BuildBremenaValidationReportInput {
  html: string;
  /** Catalog gaps / assertContractReady errors already collected. */
  baseErrors?: readonly string[];
  baseWarnings?: readonly string[];
  instrumentType?: BremenaContractInstrument | string | null;
  /** True when SK konečná is zapsaná v KN (GP required). */
  requiresGp?: boolean;
  gpCislo?: string | null;
  gpDatum?: string | null;
  /** Explicit free-of-charge confirmation. */
  bezuplatne?: boolean | null;
  /** Compensation amount (raw). Empty + !bezuplatne → error. */
  castkaNahrada?: string | number | null;
  owners?: readonly BremenaOwnerInput[] | null;
  /** True when coverage shares for multi-party instruments sum to 1. */
  sharesComplete?: boolean | null;
  /** Appendix list lines from compose (`Příloha č. 1 – …`). */
  appendixLines?: readonly string[] | null;
  /** fill_data / schválení for private company. */
  schvaleniOrgan?: string | null;
  usneseniCislo?: string | null;
  usneseniDatum?: string | null;
  /**
   * Instrument was already Kompletní/Odeslaná and then changed (Upravená / new version)
   * without a fresh successful prepare-for-signature validation.
   */
  requiresRevalidation?: boolean | null;
  /** Template catalog version / variant id+code for the report. */
  validatedTemplateVersion: string;
  /** Override hash (tests); default = sha256 of html. */
  documentHash?: string | null;
  validatedAt?: string | Date | null;
}

function stripHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Remove signature / podpis blocks so intentional ink blanks are not hard errors. */
function htmlWithoutSignatureBlocks(html: string): string {
  return html
    .replace(/<table[^>]*class="[^"]*podpisy[^"]*"[\s\S]*?<\/table>/gi, ' ')
    .replace(/<div[^>]*class="[^"]*podpis-blok[^"]*"[\s\S]*?<\/div>/gi, ' ')
    .replace(/V\s+_{3,}[^<]*dne\s+_{3,}/gi, ' ');
}

export function scanBremenaHtmlHardBlocks(html: string): BremenaValidationIssue[] {
  const issues: BremenaValidationIssue[] = [];
  const body = htmlWithoutSignatureBlocks(html ?? '');
  const plain = stripHtml(body);

  if (body.includes(DOPLNIT) || plain.includes(DOPLNIT)) {
    issues.push({
      code: 'doplnit',
      messageCs: `Dokument obsahuje ${DOPLNIT} — produkční PDF / podpisový balíček není povolen.`,
    });
  }
  if (body.includes(BULLET_MARK) || plain.includes(BULLET_MARK)) {
    issues.push({
      code: 'bullet_mark',
      messageCs: `Dokument obsahuje ${BULLET_MARK} — doplňte nebo odstraňte značku před podpisem.`,
    });
  }
  BLANK_UNDERSCORE_RE.lastIndex = 0;
  if (BLANK_UNDERSCORE_RE.test(plain)) {
    issues.push({
      code: 'blank_underscore',
      messageCs: 'Dokument obsahuje nevyplněné podtržítkové mezery mimo podpisové řádky.',
    });
  }
  CONFLICTING_ALT_RE.lastIndex = 0;
  if (CONFLICTING_ALT_RE.test(plain) || CONFLICTING_ALT_RE.test(body)) {
    issues.push({
      code: 'conflicting_alternatives',
      messageCs:
        'Dokument obsahuje nevyřešené alternativní odstavce (např. rada / zastupitelstvo) — zvolte jednu variantu.',
    });
  }
  return issues;
}

function isEmptyAmount(value: string | number | null | undefined): boolean {
  if (value == null) return true;
  const text = String(value).trim();
  if (!text) return true;
  if (text.includes(DOPLNIT)) return true;
  const n = Number(text.replace(/\s+/g, '').replace(',', '.'));
  if (Number.isFinite(n) && n === 0) return true;
  return false;
}

function hasPrivateCompany(owners: readonly BremenaOwnerInput[] | null | undefined): boolean {
  return (owners ?? []).some((o) => (o.CharOsKod ?? '').trim() === CHAROS_PRIVATE_COMPANY);
}

function missingAttachment(lines: readonly string[] | null | undefined): boolean {
  if (!lines || lines.length === 0) return true;
  return lines.some((line) => !String(line ?? '').trim() || String(line).includes(DOPLNIT));
}

/**
 * Build structured validation_report for generate / prepare-for-signature.
 * `readyForSignature` is true only when there are zero hard-block errors.
 */
export function buildBremenaValidationReport(
  input: BuildBremenaValidationReportInput,
): BremenaValidationReport {
  const errors: string[] = [...(input.baseErrors ?? [])];
  const warnings: string[] = [...(input.baseWarnings ?? [])];
  const pushUnique = (list: string[], message: string): void => {
    if (!list.includes(message)) list.push(message);
  };

  for (const issue of scanBremenaHtmlHardBlocks(input.html)) {
    pushUnique(errors, issue.messageCs);
  }

  const bezuplatne = input.bezuplatne === true;
  if (!bezuplatne && isEmptyAmount(input.castkaNahrada)) {
    pushUnique(
      errors,
      'Chybí výše náhrady — bez potvrzené bezúplatnosti nelze připravit k podpisu.',
    );
  }

  // SK final easement (zapsaná v KN): caller sets requiresGp from template variant.
  if (input.requiresGp === true) {
    if (!String(input.gpCislo ?? '').trim() || String(input.gpCislo).includes(DOPLNIT)) {
      pushUnique(errors, 'Chybí číslo geometrického plánu pro konečnou služebnost.');
    }
    if (!String(input.gpDatum ?? '').trim() || String(input.gpDatum).includes(DOPLNIT)) {
      pushUnique(errors, 'Chybí datum geometrického plánu pro konečnou služebnost.');
    }
  }

  if (input.appendixLines !== undefined && missingAttachment(input.appendixLines)) {
    pushUnique(errors, 'Chybí nebo je neúplná příloha smlouvy.');
  }

  const owners = input.owners ?? [];
  if (owners.length === 0) {
    pushUnique(errors, 'Chybí smluvní strana (vlastník) pro podpis.');
  } else {
    const slots = requiredSignerSlots([...owners]);
    if (slots.length === 0 || slots.every((s) => !s.label.trim() || s.label === 'Vlastník')) {
      const unnamed = owners.every((o) => !String(o.Jmeno ?? '').trim());
      if (unnamed) {
        pushUnique(errors, 'Chybí jméno smluvní strany pro podpis.');
      }
    }
    for (const owner of owners) {
      if (!String(owner.Jmeno ?? '').trim() || String(owner.Jmeno).includes(DOPLNIT)) {
        pushUnique(errors, 'Chybí jméno smluvní strany (vlastníka).');
        break;
      }
    }
  }

  if (owners.length > 1 && input.sharesComplete === false) {
    pushUnique(errors, 'Podíly smluvních stran nejsou kompletní (nesčítají 1).');
  }

  if (hasPrivateCompany(owners)) {
    const organ = String(input.schvaleniOrgan ?? '').trim();
    const cislo = String(input.usneseniCislo ?? '').trim();
    const datum = String(input.usneseniDatum ?? '').trim();
    if (!organ || organ.includes(DOPLNIT)) {
      pushUnique(
        errors,
        'Právnická osoba (s.r.o. / a.s.): chybí doložka schválení radou / valnou hromadou (orgán).',
      );
    }
    if (!cislo || cislo.includes(DOPLNIT)) {
      pushUnique(errors, 'Právnická osoba: chybí číslo usnesení rady / valné hromady.');
    }
    if (!datum || datum.includes(DOPLNIT)) {
      pushUnique(errors, 'Právnická osoba: chybí datum usnesení rady / valné hromady.');
    }
  }

  if (input.requiresRevalidation === true) {
    pushUnique(
      errors,
      'Dokument byl změněn po schválení — je nutná nová validace před podpisem / odesláním.',
    );
  }

  const documentHash = (input.documentHash ?? '').trim() || simpleDocumentHash(input.html ?? '');
  const validatedAt = input.validatedAt
    ? new Date(input.validatedAt).toISOString()
    : new Date().toISOString();

  return {
    readyForSignature: errors.length === 0,
    errors,
    warnings,
    validatedTemplateVersion: input.validatedTemplateVersion,
    validatedAt,
    documentHash,
  };
}

/** True when instrument status implies post-approval edit needing revalidation. */
export function bremenaRequiresRevalidationAfterEdit(
  instrumentStatus: string | null | undefined,
  opts?: { hadCompleteValidation?: boolean | null; googleDocEdited?: boolean | null },
): boolean {
  const status = normalizeBremenaContractStatus(instrumentStatus);
  if (opts?.googleDocEdited && (status === 'kompletni' || status === 'odeslana' || status === 'upravena')) {
    return true;
  }
  if (status === 'upravena' && opts?.hadCompleteValidation === true) {
    return true;
  }
  return false;
}

const DRAFT_WATERMARK_STYLE = `
.bremena-draft-watermark {
  position: fixed;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-28deg);
  font-size: 72pt;
  font-weight: 700;
  color: rgba(180, 40, 40, 0.18);
  letter-spacing: 0.12em;
  pointer-events: none;
  z-index: 9999;
  user-select: none;
  white-space: nowrap;
}
@media print {
  .bremena-draft-watermark { color: rgba(180, 40, 40, 0.22); }
}
`;

/** Stamp DRAFT watermark into HTML (preview / working PDF). Idempotent. */
export function stampBremenaDraftWatermark(html: string): string {
  const raw = html ?? '';
  if (raw.includes('bremena-draft-watermark')) return raw;
  const mark = `<div class="bremena-draft-watermark" aria-hidden="true">DRAFT</div>`;
  const styleTag = `<style data-bremena-draft="1">${DRAFT_WATERMARK_STYLE}</style>`;
  if (/<\/head>/i.test(raw)) {
    return raw.replace(/<\/head>/i, `${styleTag}</head>`).replace(/<body([^>]*)>/i, `<body$1>${mark}`);
  }
  if (/<body([^>]*)>/i.test(raw)) {
    return raw.replace(/<body([^>]*)>/i, `<body$1>${styleTag}${mark}`);
  }
  return `${styleTag}${mark}${raw}`;
}

export function isBremenaDraftHtml(html: string): boolean {
  return (html ?? '').includes('bremena-draft-watermark');
}

/** Lightweight non-crypto hash for browser + Node (backend should pass sha256 when available). */
function simpleDocumentHash(html: string): string {
  let h = 2166136261;
  for (let i = 0; i < html.length; i += 1) {
    h ^= html.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `fnv1a-${(h >>> 0).toString(16).padStart(8, '0')}-${html.length.toString(16)}`;
}
